/* eslint-disable eqeqeq */
import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Form,
  Tab,
  Nav,
} from "react-bootstrap";
import { faCog, faFileText } from "@fortawesome/free-solid-svg-icons";
import { Formik, validateYupSchema, yupToFormErrors } from "formik";
import { parse } from "date-fns";
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormType, SocialMediaType } from "../components/EventsComponents.js";
import { AplicativosType } from "../components/EventsComponents01.js";
import NotyfContext from "../contexts/NotyfContext.js";
import useAppDispatch from "../hooks/useAppDispatch.js";
import useLayout from "../hooks/useLayout.js";
import { eventList } from "../redux/slices/events.js";
import { schemaEventMode01 } from "./Json.js";
import {
  EventDefinitions,
  RegisterDefinitions,
  AppDefinitions,
  AppApplications,
  SideButtonPanel,
  AdvancedSettingsDefinitions,
} from "./EventsComponents01.js";
import { createImportValidationReport, getImportValidationSummary, getImportValidationToast, isFieldVisible, normalizeRfidTag, normalizeServerImportValidationError, schemaCreator, validateEmailDomain } from "../assets/functions.js";
import { handleDelete, hydrateFormFieldVacancies } from "./EventsComponents.js";
import { faReact } from "@fortawesome/free-brands-svg-icons";
import { getErrorPaths, scrollToFirstError } from "../utils/formErrorNavigation.js";

const FormikValuesObserver = ({ values, onValuesChange }) => {
  useEffect(() => {
    if (onValuesChange) onValuesChange(values);
  }, [onValuesChange, values]);

  return null;
};

function normalizeImportedFieldValue(rawValue, field) {
  if (field?.type === "checkbox" && field.allowMultipleChoice) {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((item) => (item === null || item === undefined ? "" : String(item).trim()))
        .filter((item) => item !== "");
    }

    if (
      rawValue === undefined ||
      rawValue === null ||
      rawValue === "" ||
      rawValue === false ||
      rawValue === "false"
    ) {
      return [];
    }

    const parseAsArray = (input) => {
      try {
        const parsed = JSON.parse(input);
        if (Array.isArray(parsed)) return parsed;

        if (typeof parsed === "string") {
          const reparsed = JSON.parse(parsed);
          if (Array.isArray(reparsed)) return reparsed;
        }
      } catch (e) {
        return null;
      }

      return null;
    };

    let valueAsText = String(rawValue).trim();

    if (valueAsText.startsWith("'") && valueAsText.endsWith("'")) {
      valueAsText = valueAsText.slice(1, -1).trim();
    }

    const parsingCandidates = [valueAsText];
    if (valueAsText.startsWith('"') && valueAsText.endsWith('"')) {
      parsingCandidates.push(valueAsText.slice(1, -1).replace(/""/g, '"').trim());
    }

    for (const candidate of parsingCandidates) {
      const parsedArray = parseAsArray(candidate);
      if (Array.isArray(parsedArray)) {
        return parsedArray
          .map((item) => (item === null || item === undefined ? "" : String(item).trim()))
          .filter((item) => item !== "");
      }
    }

    if (valueAsText.startsWith("[") && valueAsText.endsWith("]")) {
      return valueAsText
        .slice(1, -1)
        .split(",")
        .map((item) => item.trim().replace(/^['\"]|['\"]$/g, ""))
        .filter((item) => item !== "");
    }

    return [valueAsText];
  }

  if ((field?.rfigtag === true || field?.validation === "rfid") && typeof rawValue === "string" && rawValue.trim() !== "") {
    return normalizeRfidTag(rawValue);
  }

  return rawValue;
}

export const FormComponentMode01 = ({
  fonts,
  dataTable,
  aplicativos,
  dataAddForm,
  setDataAddForm,
  applications,
  setApplications,
  dataAplicativo,
  setDataAplicativo,
  modal,
  setModal,
  setDataTableUpdate,
  onValuesChange,
}) => {
  const dispatch = useAppDispatch();
  const notyf = useContext(NotyfContext);
  const { setLoading } = useLayout();
  const [activeTab, setActiveTab] = useState("form");
  const persistedFields = useMemo(
    () => hydrateFormFieldVacancies(
      dataTable.json?.form?.fields || [],
      dataTable.form_field_vacancies,
    ),
    [dataTable.form_field_vacancies, dataTable.json?.form?.fields],
  );
  const hasUnsavedPreviewData =
    JSON.stringify(dataAddForm.fields ?? []) !== JSON.stringify(persistedFields)
    || JSON.stringify(dataAddForm.fieldsToShow ?? []) !== JSON.stringify(dataTable.json?.form?.fieldsToShow ?? [])
    || JSON.stringify(dataAddForm.socialMedia ?? []) !== JSON.stringify(dataTable.json?.layout?.footer?.socialMedia ?? [])
    || JSON.stringify(applications ?? []) !== JSON.stringify(dataTable.aplicativos ?? []);

  function handlePreviewError(error) {
    notyf.open({
      type: "danger",
      message: error,
      ripple: true,
      dismissible: true,
    });
  }

  const navigateToValidationError = (validationErrors) => {
    const errorPaths = getErrorPaths(validationErrors);
    if (errorPaths.length === 0) return false;

    notyf.open({
      type: "danger",
      message: "Existem campos inválidos no formulário",
      ripple: true,
      dismissible: true,
    });

    if (!scrollToFirstError(errorPaths)) {
      const targetTab = errorPaths[0].startsWith("json.form")
        ? "form"
        : "default";
      setActiveTab(targetTab);

      const retryScroll = (remainingAttempts) => {
        window.setTimeout(() => {
          if (!scrollToFirstError(errorPaths) && remainingAttempts > 1) {
            retryScroll(remainingAttempts - 1);
          }
        }, 150);
      };

      retryScroll(4);
    }

    return true;
  };

  // Handle custom submit from side panel
  const handleCustomSubmit = async (applyOnly, values, actions) => {
    // Use the same validation logic as the form submit
    const { validateForm } = actions;
    const validationErrors = await validateForm();

    if (navigateToValidationError(validationErrors)) return;

    // Only call updateEvent01 if validation passes
    updateEvent01(
      values,
      actions,
      dataAddForm,
      setDataAddForm,
      applications,
      dataTable,
      setLoading,
      notyf,
      dispatch,
      applyOnly,
    );
  };

  return (
    <Formik
      validate={(values) => {
        // Define initialValues for schema
        const initialValues = {
          user_id: dataTable.user_id,
          evento_id: dataTable.evento_id,
          titulo: dataTable.titulo,
          descricao: dataTable.descricao,
          json: dataTable.json,
          status: dataTable.status,
          data_criacao: dataTable.data_criacao,
          data_termino: dataTable.data_termino,
          token: dataTable.token,
          codigo_presenter: dataTable.codigo_presenter,
        };

        // Get the schema function result
        const validationSchema = schemaEventMode01(
          values,
          initialValues,
          dataTable.total_participantes,
        );

        try {
          validateYupSchema(values, validationSchema, true, {});
        } catch (err) {
          return yupToFormErrors(err);
        }

        // Additional custom validations based on business logic
        const errors = {};

        return errors;
      }}
      context={{ dataTable, dataAddForm, applications }}
      enableReinitialize
      onSubmit={(values, actions, event) => {
        // Check if the "Aplicar Alterações" button was clicked
        const applyOnly = event?.nativeEvent?.submitter?.name === "applyOnly";
        updateEvent01(
          values,
          actions,
          dataAddForm,
          setDataAddForm,
          applications,
          dataTable,
          setLoading,
          notyf,
          dispatch,
          applyOnly,
        );
      }}
      initialValues={{
        user_id: dataTable.user_id,
        evento_id: dataTable.evento_id,
        titulo: dataTable.titulo,
        descricao: dataTable.descricao,
        json: dataTable.json,
        status: dataTable.status,
        data_criacao: dataTable.data_criacao,
        data_termino: dataTable.data_termino,
        token: dataTable.token,
        codigo_presenter: dataTable.codigo_presenter,
      }}
    >
      {({
        handleChange,
        values,
        touched,
        validateForm,
        errors,
        dirty,
        isSubmitting,
        setFieldValue,
        setSubmitting,
      }) => (
        <>
          <FormikValuesObserver values={values} onValuesChange={onValuesChange} />
          {modal.showModalAplicativos && (
            <AplicativosType
              applications={applications}
              setApplications={setApplications}
              dataAplicativo={dataAplicativo}
              setDataAplicativo={setDataAplicativo}
              modal={modal}
              setModal={setModal}
              aplicativos={aplicativos}
            ></AplicativosType>
          )}
          {modal.showModalForm && (
            <FormType
              platform={dataTable.platform}
              dataAddForm={dataAddForm}
              setDataAddForm={setDataAddForm}
              modal={modal}
              setModal={setModal}
              formData={values}
            />
          )}
          {modal.showSocialMediaForm && (
            <SocialMediaType
              dataAddForm={dataAddForm}
              setDataAddForm={setDataAddForm}
              modal={modal}
              setModal={setModal}
            />
          )}
          <Form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              handleCustomSubmit(
                event.nativeEvent?.submitter?.name === "applyOnly",
                values,
                { setSubmitting, validateForm },
              );
            }}
          >
            <div className="tab mt-3">
              <SideButtonPanel
                dataTable={dataTable}
                hasUnsavedChanges={dirty || hasUnsavedPreviewData}
                isSubmitting={isSubmitting}
                onSubmit={(applyOnly) =>
                  handleCustomSubmit(applyOnly, values, {
                    setSubmitting,
                    validateForm,
                  })
                }
                setDataTableUpdate={setDataTableUpdate}
              ></SideButtonPanel>
              <EventDefinitions
                dataTable={dataTable}
                values={values}
                touched={touched}
                errors={errors}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                setActiveTab={setActiveTab}
              ></EventDefinitions>
              <Tab.Container
                id="left-tabs-example"
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
              >
                <Nav variant="tabs" className="">
                  <Nav.Item>
                    <Nav.Link eventKey={"form"} style={{
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center"
                    }}>
                      <FontAwesomeIcon icon={faFileText} className="me-1" /> Definições de registro
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={"default"} style={{
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center"
                    }}>
                      <FontAwesomeIcon icon={faReact} className="me-1" /> Aplicativos ({applications.length})
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={"advanced"} style={{
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center"
                    }}>
                      <FontAwesomeIcon icon={faCog} className="me-1" /> Configurações
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey={"form"} className="mt-3">
                    <RegisterDefinitions
                      dataTable={dataTable}
                      values={values}
                      touched={touched}
                      errors={errors}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      setDataAddForm={setDataAddForm}
                      dataAddForm={dataAddForm}
                      setModal={setModal}
                    ></RegisterDefinitions>
                  </Tab.Pane>
                  <Tab.Pane eventKey={"default"} className="mt-3">
                    <AppDefinitions
                      dataTable={dataTable}
                      values={values}
                      touched={touched}
                      errors={errors}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      setDataAddForm={setDataAddForm}
                      dataAddForm={dataAddForm}
                      handlePreviewError={handlePreviewError}
                      fonts={fonts}
                      setModal={setModal}
                      applications={applications}
                      setApplications={setApplications}
                      setDataAplicativo={setDataAplicativo}
                    ></AppDefinitions>
                  </Tab.Pane>
                  <Tab.Pane eventKey={"advanced"} className="mt-3">
                    <AdvancedSettingsDefinitions
                      values={values}
                      touched={touched}
                      errors={errors}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      onDelete={() =>
                        handleDelete(
                          dataTable,
                          dataTable.platform,
                          setLoading,
                          notyf,
                          dispatch,
                          eventList,
                        )
                      }
                    ></AdvancedSettingsDefinitions>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
};

export async function updateEvent01(
  values,
  actions,
  dataForm,
  setDataAddForm,
  applications,
  dataTable,
  setLoading,
  notyf,
  dispatch,
  applyOnly = false,
) {
  if (dataForm.importUsers.error) {
    actions.setSubmitting(false);
    notyf.open({
      type: "danger",
      message: "Corrija os erros da importação antes de salvar o evento.",
      ripple: true,
      dismissible: true,
    });
    return;
  }
  setLoading(true);
  let ids = [];
  let newObjectApplications = [];
  let formattedDataVisitantes = [];
  const updatedValues = JSON.parse(JSON.stringify(values));

  if (dataForm.fieldsToShow.length <= 0) {
    setLoading(false);
    actions.setSubmitting(false);
    notyf.open({
      type: "danger",
      message: `Identifique ao menos 1 dos campos dos formulário como "Campo de identificação"`,
      ripple: true,
      dismissible: true,
    });
    return; // Exit the function immediately
  }

  updatedValues.json.form.fields = dataForm.fields;
  updatedValues.json.form.fieldsToShow = dataForm.fieldsToShow;
  updatedValues.json.layout.footer.socialMedia = dataForm.socialMedia;

  applications.forEach((element) => {
    newObjectApplications.push({
      aplicativo_id: element.aplicativo_id,
      customizacao_id: element.customizacao_id,
      version: element.version,
    });
    ids.push(element.aplicativo_id);
  });

  // All validation already ran at file-pick time in handleFileUpload.
  // Here we just reshape each row into the { inputID: { label, value } } format
  // expected by the API (formattedDataVisitantes).
  if (dataForm.importUsers.users.length > 0) {
    for (const row of dataForm.importUsers.users) {
      const rowObject = {};
      const valuesByField = updatedValues.json.form.fields.reduce((result, field) => {
        result[field.inputID] = normalizeImportedFieldValue(row[field.label], field);
        return result;
      }, {});
      for (const key of Object.keys(row)) {
        const matchedField = updatedValues.json.form.fields.find(
          (field) => field.label === key,
        );
        if (matchedField && isFieldVisible(matchedField, updatedValues.json.form.fields, valuesByField)) {
          const normalizedValue = normalizeImportedFieldValue(row[key], matchedField);
          rowObject[matchedField.inputID] = {
            label: key,
            value: normalizedValue,
          };
        } else if (key === "email") {
          rowObject.email = row[key];
        }
      }
      rowObject.__importRow = row.__importRow;
      formattedDataVisitantes.push(rowObject);
    }
  }


  // Parse the date assuming it's in Brasília timezone (GMT-3)
  const formattedDate = parse(
    updatedValues.json.form.register_start_date,
    "dd/MM/yyyy HH:mm",
    new Date(),
  );
  // Convert from Brasília time to UTC (this will add 3 hours)
  const startDateUTC = zonedTimeToUtc(formattedDate, "America/Sao_Paulo");
  updatedValues.json.form.register_start_date = formatInTimeZone(
    startDateUTC,
    "UTC",
    "yyyy-MM-dd HH:mm",
  );

  const formattedDate2 = parse(
    updatedValues.json.form.register_end_date,
    "dd/MM/yyyy HH:mm",
    new Date(),
  );
  // Convert from Brasília time to UTC (this will add 3 hours)
  const endDateUTC = zonedTimeToUtc(formattedDate2, "America/Sao_Paulo");
  updatedValues.json.form.register_end_date = formatInTimeZone(
    endDateUTC,
    "UTC",
    "yyyy-MM-dd HH:mm",
  );

  axios
    .put(`/api/event/${dataTable.evento_id}`, {
      user_id: values.user_id,
      titulo: updatedValues.titulo,
      descricao: updatedValues.descricao,
      status: values.status == 0 ? 2 : values.status,
      json: JSON.stringify(updatedValues.json),
      data_termino: values.data_termino,
      applications: JSON.stringify(newObjectApplications),
      platform: 1,
      addVisitantes: JSON.stringify(formattedDataVisitantes),
      disableValidation: dataForm.importUsers.disableValidation ? 1 : 0,
    })
    .then(function (response) {
      setLoading(false);
      if (response.status === 201) {
        notyf.open({
          type: "success",
          message: applyOnly
            ? "Alterações aplicadas com sucesso"
            : "Evento salvo com sucesso",
          ripple: true,
          dismissible: true,
        });
        if (!applyOnly) {
          dispatch(eventList());
        } else {
          const startDateSaoPaulo = utcToZonedTime(
            startDateUTC,
            "America/Sao_Paulo",
          );
          const endDateSaoPaulo = utcToZonedTime(
            endDateUTC,
            "America/Sao_Paulo",
          );
          updatedValues.json.form.register_start_date = formatInTimeZone(
            startDateSaoPaulo,
            "America/Sao_Paulo",
            "dd/MM/yyyy HH:mm",
          );
          updatedValues.json.form.register_end_date = formatInTimeZone(
            endDateSaoPaulo,
            "America/Sao_Paulo",
            "dd/MM/yyyy HH:mm",
          );

          // Refetch the data to ensure state security and consistency
          dispatch(eventList({ fetch: true, keepOpen: true }));
        }
      } else {
        actions.setSubmitting(false);
        notyf.open({
          type: "danger",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      }
    })
    .catch(function (error) {
      setLoading(false);
      actions.setSubmitting(false);
      const importErrors = error?.response?.data?.errors;
      const errors = Array.isArray(importErrors)
        ? importErrors.map((item) => normalizeServerImportValidationError(
          item,
          dataForm.importUsers.users[Number(item.row_index)]?.__importRow || null,
        ))
        : [];
      if (errors.length > 0) {
        const summary = getImportValidationSummary(errors);
        setDataAddForm((previous) => ({
          ...previous,
          importUsers: {
            ...previous.importUsers,
            error: summary,
            errorList: errors,
            report: createImportValidationReport(errors, summary),
          },
        }));
      }
      notyf.open({
        type: "danger",
        message: getImportValidationToast(errors, error?.response?.data?.code || "EVENT_UPDATE_FAILED"),
        ripple: true,
        dismissible: true,
      });
    });
}
